import React, { useState } from 'react';
import Head from 'next/head';
import Header from './../../components/Header';
import Footer from './../../components/Footer';

function countSentences(paragraph: string) {
    if (!paragraph) {
        return 0
    }
    const lines = paragraph.split(/\n/); // Split the paragraph into separate lines
    let sentenceCount = 0;

    for (const line of lines) {
        const sentenceEndRegex = /[.|!|?|…|;]+['")\]]*\s*/g; // Matches sentence-ending punctuation and closing punctuation, followed by optional closing quotes or brackets and zero or more spaces
        const sentenceEndMatches = line.match(sentenceEndRegex);
        const lineSentenceCount = sentenceEndMatches ? sentenceEndMatches.length : 0;
        sentenceCount += lineSentenceCount;
    }

    return sentenceCount;
}

function paragraphSplit(answer: string) {
    return answer.split(/(?:\r?\n){1,}\s*/).filter(p => p.trim() !== '');
}

const IELTSWordCounter = ({ }) => {
    const [text, setText] = useState('');

    const handleTextChange = (event: any) => {
        setText(event.target.value);
    };

    const wordCount = text.split(/\s+/).filter((word) => word).length;
    const paragraphCount = paragraphSplit(text).length;
    const charCount = text.replace(/\s+/g, '').length;
    const sentenceCount = countSentences(text);

    return (
        <div className="flex flex-col min-h-screen justify-between">
            <Head>
                <title>Free IELTS Word Counter - Interactive Online App ⌨️. Words count. Paragraphs count.</title>
                <meta name="description" content="Check IELTS Writing word count online, character count, sentence count, and paragraph count with our free online tool." />
            </Head>

            <Header />

            <div className="p-4 max-w-3xl m-auto">
                <h1 className="text-3xl font-semibold mb-4">Free IELTS Word Counter</h1>
                <p className="mb-4">IELTS writing task 2 requires at least 250 words, high scoring essays usually have 270-320 words. This free tool will help you to count words, characters, sentences, and paragraphs in your IELTS Writing Task 2 essays. Just paste your text in the box below:</p>

                <div className="mt-4 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
                    <div className="p-4 bg-white rounded shadow text-center hover:shadow-lg transition-shadow duration-200 ease-in">
                        <p className="text-3xl font-bold">{wordCount}</p>
                        <p>Words</p>
                    </div>
                    <div className="p-4 bg-white rounded shadow text-center hover:shadow-lg transition-shadow duration-200 ease-in">
                        <p className="text-3xl font-bold">{charCount}</p>
                        <p>Characters</p>
                    </div>
                    <div className="p-4 bg-white rounded shadow text-center hover:shadow-lg transition-shadow duration-200 ease-in">
                        <p className="text-3xl font-bold">{sentenceCount}</p>
                        <p>Sentences</p>
                    </div>
                    <div className="p-4 bg-white rounded shadow text-center hover:shadow-lg transition-shadow duration-200 ease-in">
                        <p className="text-3xl font-bold">{paragraphCount}</p>
                        <p>Paragraphs</p>
                    </div>
                </div>

                <textarea
                    className="w-full h-64 p-2 border border-blue-300 rounded shadow-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-200 ease-in"
                    value={text}
                    onChange={handleTextChange}
                    placeholder="Paste your text here..."
                />

                <div className="text-left mt-8 space-y-6">

                    <h2 className="text-2xl font-semibold mt-8">How does IELTS count words?</h2>
                    <p className="mt-4">IELTS counts all words, including small ones like 'a', 'an', 'the'. Compound words (e.g. 'well-being'), hyphenated words, numbers, and dates are counted as one. If your response contains 20 words or fewer, it's rated at Band 1. Typically a good essay has 270-320 words.</p>

                    <h2 className="text-2xl font-semibold mt-8">Why are paragraphs important in IELTS Writing?</h2>
                    <p className="mt-4">Paragraphing makes your essay clear and easy to read. Each new idea or topic should start a new paragraph. If paragraphing is inadequate, your Coherence and Cohesion score could be limited to 5. Typically, a Task 2 essay should have 4-5 paragraphs.</p>

                    <h2 className="text-2xl font-semibold mt-8">Why should I count sentences in my essay?</h2>
                    <p className="mt-4">The number of sentences affects the clarity and coherence of your essay. Each paragraph should have a minimum of 3-4 sentences, including a topic sentence and supporting sentences. Varying sentence lengths keeps your writing engaging, but avoid very long sentences to prevent confusion.</p>

                    <h2 className="text-2xl font-semibold mt-8">Does IELTS count characters in my essay?</h2>
                    <p className="mt-4">While IELTS doesn't directly count characters, it's a good measure of your writing's length. More characters usually mean more words, helping you meet the minimum word requirement for Task 2.</p>

                </div>

            </div>

            <Footer />
        </div>
    );
};

export default IELTSWordCounter;